import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const SELF_REPORT_LABELS = {
  EASY: '++',
  OK_NO_PEEK: '+',
  OK_WITH_PEEK: '+-',
  OK_WITH_QUESTION: '+?',
  FAILED: '-',
  NO_IDEAS: '--',
  NOT_TRIED: '!',
};

function groupAttemptsByDay(attemptsList) {
  const groups = new Map();
  for (const a of attemptsList || []) {
    const key = a?.created_at ? new Date(a.created_at).toLocaleDateString('ru-RU') : '—';
    const arr = groups.get(key) || [];
    arr.push(a);
    groups.set(key, arr);
  }
  return Array.from(groups.entries());
}

/**
 * Журнал попыток ученика с фильтрами. Списки задач передаются с overview для scope.
 */
export default function TeacherStudentActivityJournal({
  studentId,
  myLists = [],
  otherLists = [],
  selfLists = [],
  onSelectTaskSlug,
}) {
  const [journalDays, setJournalDays] = useState(30);
  const [journalProblemOnly, setJournalProblemOnly] = useState(false);
  const [journalListScope, setJournalListScope] = useState('all');
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState('');
  const [journalAttempts, setJournalAttempts] = useState([]);

  const scopeToListIds = useCallback(() => {
    if (journalListScope === 'mine') return myLists.map((l) => l.id);
    if (journalListScope === 'other') return otherLists.map((l) => l.id);
    if (journalListScope === 'self') return selfLists.map((l) => l.id);
    return [...myLists, ...otherLists, ...selfLists].map((l) => l.id);
  }, [journalListScope, myLists, otherLists, selfLists]);

  const loadJournal = useCallback(async () => {
    if (!studentId) return;
    setJournalLoading(true);
    setJournalError('');
    try {
      const listIds = scopeToListIds();
      const baseParams = {
        days: journalDays === 0 ? undefined : journalDays,
        problem: journalProblemOnly ? 1 : undefined,
      };
      const requests = listIds.length > 0
        ? listIds.map((id) => api.get(`/teacher/students/${studentId}/attempts/`, { params: { ...baseParams, task_list: id } }))
        : [api.get(`/teacher/students/${studentId}/attempts/`, { params: baseParams })];
      const resList = await Promise.all(requests);
      const merged = resList
        .flatMap((r) => (Array.isArray(r.data) ? r.data : (r.data?.results ?? [])))
        .sort((a, b) => (String(b.created_at || '')).localeCompare(String(a.created_at || '')));
      const seen = new Set();
      const uniq = [];
      for (const a of merged) {
        if (!a?.id || seen.has(a.id)) continue;
        seen.add(a.id);
        uniq.push(a);
      }
      setJournalAttempts(uniq.slice(0, 200));
    } catch {
      setJournalError('Не удалось загрузить журнал активности.');
      setJournalAttempts([]);
    } finally {
      setJournalLoading(false);
    }
  }, [studentId, journalDays, journalProblemOnly, scopeToListIds]);

  useEffect(() => {
    void loadJournal();
  }, [studentId, journalDays, journalProblemOnly, journalListScope, myLists.length, otherLists.length, selfLists.length, loadJournal]);

  return (
    <div className="teacher-activity-journal">
      {journalError && <div className="alert alert-error">{journalError}</div>}

      <div className="form-group" style={{ display: 'grid', gap: '0.5rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <label className="text-secondary" style={{ fontSize: '0.85rem' }}>Период:</label>
          <select className="form-select" value={journalDays} onChange={(e) => setJournalDays(Number(e.target.value))}>
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
            <option value={0}>всё время</option>
          </select>

          <label className="text-secondary" style={{ fontSize: '0.85rem' }}>Списки:</label>
          <select className="form-select" value={journalListScope} onChange={(e) => setJournalListScope(e.target.value)}>
            <option value="all">все</option>
            <option value="mine">выдано мной</option>
            <option value="other">другие преподаватели</option>
            <option value="self">самостоятельно</option>
          </select>
        </div>

        <label style={{ fontSize: '0.9rem' }}>
          <input type="checkbox" checked={journalProblemOnly} onChange={(e) => setJournalProblemOnly(e.target.checked)} /> проблемные
        </label>

        <button type="button" className="btn btn-xs btn-outline" disabled={journalLoading} onClick={() => void loadJournal()}>
          {journalLoading ? 'Загрузка…' : 'Обновить'}
        </button>
      </div>

      {journalLoading ? (
        <p className="text-secondary">Загрузка журнала…</p>
      ) : journalAttempts.length === 0 ? (
        <p className="text-secondary">Пока нет событий.</p>
      ) : (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {groupAttemptsByDay(journalAttempts).map(([day, items]) => (
            <div key={day}>
              <div className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>{day}</div>
              <ul className="recent-attempts-list">
                {items.slice(0, 20).map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className="link-like"
                      onClick={() => onSelectTaskSlug?.(a.task_slug)}
                      title={a.task_list_title ? `Список: ${a.task_list_title}` : ''}
                    >
                      {a.task_title || a.task_slug}{' '}
                      <span className="badge-small">{SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || '—'}</span>
                      {a.task_list_title ? <span className="text-secondary" style={{ marginLeft: '0.35rem' }}>• {a.task_list_title}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
